// why: Template editor for the global default prep base schedule.
// Edits here ONLY affect new schedules created afterward (the wizard's Step 2
// uses this as the starting grid). Existing schedules carry their own
// per-schedule base — edit via the Schedule Detail page if those need to
// change. No Recompute button: under v2 each schedule's prep grid is
// independent, so a global recompute from this tab would have unclear blast
// radius. To re-derive a single schedule's combos, use the Recompute action
// on that schedule's detail panel.
import { useMutation, useQuery } from '@apollo/client'
import SaveIcon from '@mui/icons-material/Save'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Typography,
} from '@mui/material'
import React, { useEffect, useMemo, useState } from 'react'

import {
  IPrepItemScheduleConfig2Type,
  IPrepItemWeekdayConfig2Type,
  IWeekdayInput,
  editPrepItemWeekdayConfig2,
  getPrepItemScheduleConfig2,
  getPrepItemWeekdayConfig2,
} from '../../PrepItemConfiguration/gql'

type DayKey =
  | 'sunday'
  | 'monday'
  | 'tuesday'
  | 'wednesday'
  | 'thursday'
  | 'friday'
  | 'saturday'

const DAY_KEYS: DayKey[] = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
]
const DAY_HEADERS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

interface GridRow {
  prepItemIdCode: string
  prepItemName: string
  sunday: boolean
  monday: boolean
  tuesday: boolean
  wednesday: boolean
  thursday: boolean
  friday: boolean
  saturday: boolean
}

const DefaultScheduleTab: React.FC = () => {
  const today = useMemo(() => new Date().toISOString().split('T')[0], [])
  const { data, loading, refetch } = useQuery<IPrepItemWeekdayConfig2Type>(
    getPrepItemWeekdayConfig2,
    { variables: { iQueryDate: today, iLocationId: 0 } },
  )
  const { data: prepBaseData } = useQuery<IPrepItemScheduleConfig2Type>(
    getPrepItemScheduleConfig2,
  )

  const [editGrid, setEditGrid] = useState<GridRow[]>([])
  const [dirty, setDirty] = useState(false)
  const [saving, setSaving] = useState(false)
  const [resultMessage, setResultMessage] = useState<string | null>(null)

  const [editMutation] = useMutation(editPrepItemWeekdayConfig2)

  // why: the Template lives under the default row in prep_item_schedule_config_2.
  // Resolve its id once; mutations key by schedule_id.
  // why: PostGraphile's auto-generated Condition type doesn't include
  // deletedAt (column isn't indexed), so we fetch all rows and filter
  // soft-deletes client-side.
  const activeSchedules =
    prepBaseData?.prepItemScheduleConfig2s?.nodes?.filter(
      (n) => !n.deletedAt,
    ) ?? []
  const templateScheduleId =
    activeSchedules.find((n) => n.isDefault)?.id ?? activeSchedules[0]?.id

  const initialRows = useMemo(() => {
    const nodes = data?.getPrepItemWeekdayConfig2?.nodes ?? []
    return nodes.map((n) => ({
      prepItemIdCode: n.prepItemIdCode,
      prepItemName: n.prepItemName,
      sunday: n.sunday,
      monday: n.monday,
      tuesday: n.tuesday,
      wednesday: n.wednesday,
      thursday: n.thursday,
      friday: n.friday,
      saturday: n.saturday,
    }))
  }, [data])

  useEffect(() => {
    setEditGrid(initialRows.map((r) => ({ ...r })))
    setDirty(false)
  }, [initialRows])

  const toggleCell = (rowIdx: number, day: DayKey) => {
    setEditGrid((prev) =>
      prev.map((r, i) => (i === rowIdx ? { ...r, [day]: !r[day] } : r)),
    )
    setDirty(true)
  }

  const handleSave = async () => {
    if (!templateScheduleId) return
    setSaving(true)
    try {
      const weekdays: IWeekdayInput[] = editGrid.map((r) => ({
        prep_item_id: r.prepItemIdCode,
        sunday: r.sunday,
        monday: r.monday,
        tuesday: r.tuesday,
        wednesday: r.wednesday,
        thursday: r.thursday,
        friday: r.friday,
        saturday: r.saturday,
      }))
      await editMutation({
        variables: {
          iScheduleId: templateScheduleId,
          iLocationId: 0,
          iWeekdays: weekdays,
        },
      })
      setDirty(false)
      setResultMessage(
        'Template saved. Existing schedules keep their current grids. New schedules created after this point will start from the updated Template.',
      )
      await refetch()
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <CircularProgress size={32} />
      </Box>
    )
  }

  if (editGrid.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">
          No Template schedule found. Use the Prep Item Configuration page to
          create one first.
        </Alert>
      </Box>
    )
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Typography variant="h6" sx={{ flex: 1 }}>
          Template Prep Schedule
        </Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
          onClick={handleSave}
          disabled={!dirty || saving || !templateScheduleId}
          sx={{ textTransform: 'none' }}
        >
          Save
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Editing this Template only affects schedules created AFTER you save.
        Each existing schedule carries its own copy of the Prep Base — edit it
        from the schedule's detail page if you want to change an existing one.
      </Typography>

      {resultMessage && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setResultMessage(null)}
        >
          {resultMessage}
        </Alert>
      )}

      <Box
        sx={{
          border: '1px solid #e0e0e0',
          borderRadius: 1,
          overflow: 'auto',
        }}
      >
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: 13,
            minWidth: 600,
          }}
        >
          <thead>
            <tr style={{ backgroundColor: '#f5f5f5' }}>
              <th
                style={{
                  textAlign: 'left',
                  padding: '6px 12px',
                  borderBottom: '2px solid #e0e0e0',
                  minWidth: 160,
                }}
              >
                Prep Item
              </th>
              {DAY_HEADERS.map((d) => (
                <th
                  key={d}
                  style={{
                    textAlign: 'center',
                    padding: '6px 4px',
                    borderBottom: '2px solid #e0e0e0',
                    width: 55,
                  }}
                >
                  {d}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {editGrid.map((row, rowIdx) => (
              <tr key={row.prepItemIdCode}>
                <td
                  style={{
                    padding: '4px 12px',
                    borderBottom: '1px solid #eee',
                  }}
                >
                  {row.prepItemName}
                </td>
                {DAY_KEYS.map((day) => (
                  <td
                    key={day}
                    style={{
                      padding: 0,
                      textAlign: 'center',
                      borderBottom: '1px solid #eee',
                    }}
                  >
                    <Checkbox
                      checked={!!row[day]}
                      onChange={() => toggleCell(rowIdx, day)}
                      size="small"
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </Box>
    </Box>
  )
}

export default DefaultScheduleTab
